import {
  APIFieldType,
  type APIFieldAttributes,
  type ArdoqId,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import type { PermissionContext } from '@ardoq/access-control';
import {
  getFieldInstancesForViewpoint,
  getRelevantFieldInstances,
  toFieldsForViewpoint,
} from 'streams/fields/getFieldsForViewpoint';
import { toFieldOptions } from 'tabview/chartShared/fieldLabel';
import type { FieldOption } from 'tabview/chartShared/types';
import { fieldOperations } from '@ardoq/core';
import {
  CATEGORY_HEIGHT_FIRST_POSITION,
  toCategoryAxis,
  toCategoryHeightScale,
  toCategoryScale,
  type AxisScale,
  type AxisScales,
  type GridResolution,
  type HorizontalAxes,
  type RawComponentValues,
  type XyzComponentReader,
  type XyzFieldSelection,
} from '@ardoq/xyz-charts';

/** The field types the field charts plot: numbers, and single-value lists read in their order. */
export const FIELD_CHART_FIELD_TYPES: APIFieldType[] = [
  APIFieldType.NUMBER,
  APIFieldType.LIST,
];

/** Each list field's values in the order the field defines them, by field name. */
export type ListValuesByField = ReadonlyMap<string, string[]>;

/**
 * Two orderings of one list merged into one: each value the second adds goes just after the value
 * it follows there, so a workspace's extra step lands between its neighbors rather than at the end.
 */
export const mergeListOrders = (merged: string[], values: string[]): string[] =>
  values.reduce((result, value, at) => {
    if (result.includes(value)) return result;
    const after = at === 0 ? -1 : result.indexOf(values[at - 1]);
    return [...result.slice(0, after + 1), value, ...result.slice(after + 1)];
  }, merged);

/**
 * Each list field's values, from every workspace's definition of it, merged so a value one
 * workspace's list adds to another's still has a position in order. A field that is a list in
 * one workspace and another type in the next is left out, and read as its raw value.
 */
export const toListValuesByField = (
  fields: APIFieldAttributes[]
): ListValuesByField => {
  const byName = Map.groupBy(fields, ({ name }) => name);
  return new Map(
    [...byName].flatMap(([name, definitions]) =>
      definitions.every(({ type }) => type === APIFieldType.LIST)
        ? [
            [
              name,
              definitions
                .map(definition =>
                  fieldOperations.getListFieldOptions(definition)
                )
                .reduce(mergeListOrders, []),
            ] as const,
          ]
        : []
    )
  );
};

export type AxisFieldsArgs = {
  scopeData: EnhancedScopeData;
  permissionContext: PermissionContext;
  workspaceIds: ArdoqId[];
  relevantComponentTypeIds: Set<ArdoqId>;
  fieldTypes: APIFieldType[];
  fieldSelection: Pick<
    XyzFieldSelection,
    'selectedFieldNameX' | 'selectedFieldNameY' | 'selectedFieldNameZ'
  >;
};

export type AxisFields = {
  /** The fields the pickers offer, by name and label. */
  axisFields: FieldOption[];
  listValues: ListValuesByField;
  listAxes: ListAxes;
};

/** The fields the axis pickers offer, and how the list fields among them are read. */
export const toAxisFields = ({
  scopeData,
  permissionContext,
  workspaceIds,
  relevantComponentTypeIds,
  fieldTypes,
  fieldSelection,
}: AxisFieldsArgs): AxisFields => {
  const instances = getRelevantFieldInstances(
    getFieldInstancesForViewpoint({
      scopeData,
      workspaceIds,
      types: fieldTypes,
    }),
    relevantComponentTypeIds
  );
  const listValues = toListValuesByField(instances.map(({ field }) => field));
  return {
    axisFields: toFieldOptions(
      toFieldsForViewpoint({
        permissionContext,
        fieldInstances: instances,
        relevantComponentTypeIds,
      })
    ),
    listValues,
    listAxes: toListAxes(fieldSelection, listValues),
  };
};

/**
 * The reader with each list value read as its position in the list's order, so the charts plot a
 * list field on the same footing as a number. A value the list no longer defines reads as nothing,
 * and the component is left out for it like one with no value.
 */
export const withListPositions = (
  reader: XyzComponentReader,
  listValues: ListValuesByField
): XyzComponentReader => ({
  ...reader,
  getFieldValue: (componentId, fieldName) => {
    const value = reader.getFieldValue(componentId, fieldName);
    const values = listValues.get(fieldName);
    if (!values) return value;
    const position = typeof value === 'string' ? values.indexOf(value) : -1;
    return position < 0 ? null : position;
  },
});

/** The list values on each axis, or null for an axis plotting a number. */
export type ListAxes = {
  x: string[] | null;
  y: string[] | null;
  z: string[] | null;
};

export const NO_LIST_AXES: ListAxes = { x: null, y: null, z: null };

export const toListAxes = (
  {
    selectedFieldNameX,
    selectedFieldNameY,
    selectedFieldNameZ,
  }: Pick<
    XyzFieldSelection,
    'selectedFieldNameX' | 'selectedFieldNameY' | 'selectedFieldNameZ'
  >,
  listValues: ListValuesByField
): ListAxes => ({
  x: (selectedFieldNameX && listValues.get(selectedFieldNameX)) || null,
  y: (selectedFieldNameY && listValues.get(selectedFieldNameY)) || null,
  z: (selectedFieldNameZ && listValues.get(selectedFieldNameZ)) || null,
});

/** The host's axes with each list axis replaced by its category axis. */
export const withCategoryAxes = (
  axes: HorizontalAxes,
  { x, y }: ListAxes
): HorizontalAxes => ({
  x: x ? toCategoryAxis(x) : axes.x,
  y: y ? toCategoryAxis(y) : axes.y,
});

const NUMBER_SCALE: AxisScale = { kind: 'number' };

/** Each axis's scale: categories named by the list's values on a list axis, numbers otherwise. */
export const toAxisScales = ({ x, y, z }: ListAxes): AxisScales => ({
  x: x ? toCategoryScale(x) : NUMBER_SCALE,
  y: y ? toCategoryScale(y) : NUMBER_SCALE,
  z: z ? toCategoryHeightScale(z) : NUMBER_SCALE,
});

/** A list plotted as height read from the category height axis's first position, not from 0. */
export const withListHeightPositions = (
  values: RawComponentValues[],
  { z }: ListAxes
): RawComponentValues[] =>
  z === null
    ? values
    : values.map(value =>
        typeof value.z === 'number'
          ? { ...value, z: value.z + CATEGORY_HEIGHT_FIRST_POSITION }
          : value
      );

/** The positions from lowest to highest, or nothing where fewer than two leave no grid to bin. */
const toPositionSpan = (positions: number[]): number | null => {
  if (positions.length === 0) return null;
  const span = Math.max(...positions) - Math.min(...positions) + 1;
  return span < 2 ? null : span;
};

/**
 * One bin per list value from the lowest present to the highest, on each list axis - so a binned
 * chart puts a cell on every category whatever the list's length, where a number axis caps how many
 * values it gives a bin each.
 */
export type ListBinCounts = { x: number | null; y: number | null };

export const toListBinCounts = (
  points: { x: number; y: number }[],
  { x, y }: ListAxes
): ListBinCounts => ({
  x: x ? toPositionSpan(points.map(point => point.x)) : null,
  y: y ? toPositionSpan(points.map(point => point.y)) : null,
});

/**
 * A binned grid's resolution with a bin per value on each list axis, and the given count on a
 * number axis. Unchanged where neither axis is a list.
 */
export const toListGridResolution = (
  { x, y }: ListBinCounts,
  resolution: number
): GridResolution =>
  x === null && y === null
    ? resolution
    : { x: x ?? resolution, y: y ?? resolution };
